/**
 * Area chart view component using Recharts.
 * Supports overlapping and stacked area variants.
 */

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { ChartConfig, ChartReferenceLine, TransformedChartData } from '../../types/chartTypes';
import { DEFAULT_CHART_COLORS } from '../../types/chartTypes';
import { useXAxisTickLayout } from '../../hooks/useXAxisTickLayout';
import { abbreviateAxisTick, CARTESIAN_MARGIN, formatTooltipValue } from '../../utils/axisFormat';
import { renderChartReferenceLines } from './chartReferenceLines';

type AreaChartViewProps = {
  data: TransformedChartData;
  config: ChartConfig;
  height?: number | `${number}%`;
  /** Recharts default is true; dashboard tiles animate only on first mount. */
  isAnimationActive?: boolean;
  /** Target/goal lines (dashboard tiles). */
  referenceLines?: ChartReferenceLine[];
  /** Raw mark-click passthrough (S11) — ChartRenderer normalizes/resolves. */
  onMark?: (...args: unknown[]) => void;
  /** ResponsiveContainer resize debounce (ms) — dashboard tiles pass ~150. */
  resizeDebounceMs?: number;
};

export function AreaChartView({ data, config, height = 400, isAnimationActive, referenceLines, onMark, resizeDebounceMs }: AreaChartViewProps) {
  const colors = config.colors || DEFAULT_CHART_COLORS;
  const isStacked = config.areaVariant === 'stacked';
  const { layout: xTicks, onResize } = useXAxisTickLayout(data.data, data.xDataKey);

  if (data.data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No data to display
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height} debounce={resizeDebounceMs} onResize={onResize}>
      <AreaChart
        data={data.data}
        margin={CARTESIAN_MARGIN}
        stackOffset={isStacked ? 'none' : undefined}
      >
        {config.showGrid && <CartesianGrid strokeDasharray="3 3" />}
        <XAxis
          dataKey={data.xDataKey}
          tick={{ fontSize: 12 }}
          angle={xTicks.angle}
          textAnchor={xTicks.textAnchor}
          height={xTicks.height}
        />
        {/* preserveStartEnd keeps the 0 baseline label when ticks are culled. */}
        <YAxis tick={{ fontSize: 12 }} tickFormatter={abbreviateAxisTick} interval="preserveStartEnd" />
        {renderChartReferenceLines(referenceLines)}
        <Tooltip
          formatter={(value, name) => [formatTooltipValue(value), name]}
          contentStyle={{
            backgroundColor: 'var(--background)',
            border: '1px solid var(--border)',
            borderRadius: '0.375rem',
          }}
        />
        {config.showLegend && <Legend verticalAlign="top" height={36} />}
        {data.seriesKeys.map((key, index) => (
          <Area
            key={key}
            isAnimationActive={isAnimationActive}
            type="monotone"
            dataKey={key}
            stroke={colors[index % colors.length]}
            fill={colors[index % colors.length]}
            fillOpacity={isStacked ? 0.6 : 0.3}
            name={key}
            stackId={isStacked ? 'stack' : undefined}
            activeDot={onMark ? { r: 5, onClick: onMark, cursor: 'pointer' } : undefined}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}
